import { Box, Card, Section } from '@factorialco/deprecated-design-system'
import { F0Form, f0FormField } from '@factorialco/f0-react'
import { z } from 'zod'

import { observer } from 'mobx-react'

import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import i18n from 'modules/core/lib/i18n'

import type GDPRDataRetention from 'modules/ats/store/models/GDPRDataRetention'

interface Props {
  currentRetention: GDPRDataRetention
}

const DataRetention = ({ currentRetention }: Props) => {
  const schema = z.object({
    default_retention_limit: f0FormField(z.coerce.number().int().nonnegative(), {
      label: `${i18n.t(
        'ats.settings.data_retention.permission_expiration.inputs_label.job_applications'
      )} (${i18n.t('ats.settings.data_retention.permission_expiration.months').toLocaleLowerCase(i18n.locale)})`,
    }),
    talent_pool_retention_limit: f0FormField(z.coerce.number().int().nonnegative(), {
      label: `${i18n.t(
        'ats.settings.data_retention.permission_expiration.inputs_label.talent_pool'
      )} (${i18n.t('ats.settings.data_retention.permission_expiration.months').toLocaleLowerCase(i18n.locale)})`,
    }),
  })

  const { performAction: saveToggleData } = useAsyncAction(async (value: boolean) => {
    await currentRetention?.save({
      consent_limit_enabled: value,
      ...(value === false && {
        auto_deletion_enabled: false,
        consent_renewal_email_enabled: false,
      }),
    })
  })

  const active = currentRetention?.attributes.consent_limit_enabled

  const toggle = (value: boolean) => {
    saveToggleData(value)
  }

  const handleSubmit = async (values: z.infer<typeof schema>) => {
    const updated = await currentRetention?.save(values)
    return { success: !!updated }
  }

  return (
    <Section.TwoThirds
      description={i18n.t('app_store_v2.apps.ats.settings.data_retention.description')}
      illustration="visibility"
      title={i18n.t('app_store_v2.apps.ats.settings.data_retention.title')}
      link={{
        label: i18n.t('app_store_v2.apps.ats.settings.data_retention.link'),
        href: i18n.t('app_store_v2.apps.ats.settings.data_retention.link_url'),
      }}
    >
      <Box flexDirection="column">
        <Card>
          <Card.Title size="small">
            {i18n.t('ats.settings.data_retention.permission_expiration.title')}
          </Card.Title>
          <Card.Description>
            {i18n.t('ats.settings.data_retention.permission_expiration.description')}
          </Card.Description>
          <Card.ExpandableContent expanded={active} onExpandChange={toggle}>
            <F0Form
              name="data-retention-limits"
              schema={schema}
              defaultValues={{
                default_retention_limit: currentRetention.attributes.default_retention_limit,
                talent_pool_retention_limit:
                  currentRetention.attributes.talent_pool_retention_limit,
              }}
              onSubmit={handleSubmit}
              submitConfig={{ type: 'default', label: i18n.t('common.submit') }}
            />
          </Card.ExpandableContent>
        </Card>
      </Box>
    </Section.TwoThirds>
  )
}

export default observer(DataRetention)
